import { MetaType, MetaTypesBase, TypeDefineType } from "@/autogen/common";
import {
  FilterBase,
  TableHelperType,
  HeaderType,
  ItemType,
  TableType,
  SortByType,
} from "@/composables/useTableHelper";
import { useCSVUtil } from "@/composables/useCSVUtil";
import { toRefs } from "vue";

const csvUtil = useCSVUtil();

/**
 * Get the computed value for a header cell
 * If the header has a computeValue function, use it
 * Otherwise, return the raw value
 */
/*function getComputedValue(
  header: HeaderType<ItemType>,
  item: T_Item,
  forCSV: boolean,
): string {
  if (header.computeValue) {
    return header.computeValue(item, forCSV);
  }
  return String(item[header.key] ?? "");
}

/**
 * Export a child table to CSV
 */
function exportChildTable(childTable: any) {
  const fileName = `${childTable.id.replaceAll(" ", "_")}.csv`;
  const headers = childTable.headersRaw.value.map(
    (h: HeaderType<ItemType>) => ({
      title: h.title,
      value: h.key,
      computeValue: h.computeValue,
    }),
  );

  // Use computeValue for CSV export if available
  const items = childTable.items.value.map((item: any) => {
    const csvItem: any = {};
    headers.forEach((header: any) => {
      if (header.computeValue) {
        csvItem[header.value] = header.computeValue(item, true);
      } else {
        csvItem[header.value] = item[header.value];
      }
    });
    return csvItem;
  });

  csvUtil.generateCSV(
    items,
    fileName,
    headers.map((h: any) => ({ title: h.title, value: h.value })),
  );
}

import { defineComponent, PropType } from "vue";
import { VChip, VDataTable, VDataTableServer } from "vuetify/components";
import ReportingChildTables from "./ReportingChildTables.vue";

const Table = VDataTable as unknown as Component;
const ServerTable = VDataTableServer as unknown as Component;

export function createTableComponent<
  T_Meta extends MetaType<TypeDefineType>,
  T_Item extends ItemType,
>(useServer: boolean = false) {
  return defineComponent({
    props: {
      hidePagination: Boolean,
      itemValue: String,
      table: {
        type: Object as PropType<TableType<T_Item, T_Meta>>,
        required: true,
      },
    },

    setup(props, { slots }) {
      const tableSlots: Record<string, any> = {};

      // Use toRefs to re-wrap the unwrapped refs from props
      const tableRefs = toRefs(props.table as any);

      const showExpand = computed(
        () =>
          !!slots["expanded-row"] ||
          !!tableRefs.items.value?.find(
            (a: T_Item) => !!a.childTables && a.childTables.length > 0,
          ),
      );

      function getComputeValue(header: HeaderType<T_Item>) {
        if (header.computeValue != null) return header.computeValue;
        //if(header.meta?.propertyType == MetaTypesBase)
      }

      function renderSlot(header: HeaderType<T_Item>, name: string) {
        return (slotProps: any) => {
          // 1. If parent provided slot → use it
          const parentSlot = slots[name];
          if (parentSlot) return parentSlot(slotProps);
          const value = header.computeValue
            ? header.computeValue(slotProps.item, false)
            : slotProps.value;
          if (!value && !header.renderer) return "-";
          if (header.computeValue != null) {
            if (header.computeValueColor != null) {
              return h(
                VChip,
                {
                  color: header.computeValueColor(slotProps.item),
                  size: "small",
                  value: slotProps.value,
                },
                value,
              );
            } else if (header.renderer != null) {
              return h(header.renderer, {
                item: slotProps.item,
                value: value,
              });
            } else {
              return value;
            }
          }

          // 2. Otherwise use custom renderer if defined
          if (header.renderer) {
            return h(header.renderer, {
              item: slotProps.item,
              value: slotProps.value,
            });
          }

          // 3. fallback default
          return slotProps.value;
        };
      }

      tableSlots["body.append"] = () => {
        if (!tableRefs.footerItem?.value) return null;
        const children = tableRefs.headersFiltered.value.map(
          (header: HeaderType<T_Item>) => {
            const slotName = `item.${header.key}`;
            const slots: any = {};
            slots[slotName] = renderSlot(header, slotName);

            return h(
              "td",
              {},
              renderSlot(
                header,
                slotName,
              )({
                item: tableRefs.footerItem?.value,
                value: tableRefs.footerItem?.value
                  ? tableRefs.footerItem.value[header.key]
                  : "-",
              }),
            );
          },
        );

        return h("tr", {}, children);
      };

      tableSlots["expanded-row"] =
        slots["expanded-row"] ??
        ((options: { item: T_Item; columns: any[] }) => {
          //return h("p", {}, showExpand.value);
          if (!options.item.childTables || options.item.childTables.length == 0)
            return h("p", {}, "NO CHILDREN");
          return h(ReportingChildTables, {
            childTables: options.item.childTables,
            columns: options.columns,
          });
        });
      if (slots["item.data-table-expand"]) {
        tableSlots["item.data-table-expand"] = slots["item.data-table-expand"];
      }

      return () => {
        const baseProps = {
          headers: tableRefs.headersFiltered.value,
          items: tableRefs.sortedItems?.value || tableRefs.items.value,
          loading: tableRefs.isLoading.value,
          itemValue: props.table.itemValue,
          showExpand: showExpand.value,
          hideDefaultFooter: props.hidePagination == true,
          disableSort: props.hidePagination == true,
          multiSort: true,
          ...(!useServer && !props.hidePagination && tableRefs.pagination
            ? {
                page: tableRefs.pagination.value.page,
                itemsPerPage: tableRefs.pagination.value.perPage,
                "onUpdate:page": (page: number) => {
                  tableRefs.pagination.value.page = page;
                },
                "onUpdate:itemsPerPage": (perPage: number) => {
                  tableRefs.pagination.value.perPage = perPage;
                },
                sortBy: tableRefs.sortBy?.value,
                "onUpdate:sortBy": (sortBy: SortByType[]) => {
                  if (tableRefs.sortBy) tableRefs.sortBy.value = sortBy;
                },
              }
            : {}),
          expanded: tableRefs.expandedRows.value,
          "onUpdate:expanded": (value: any[]) => {
            if (tableRefs.expandedRows) {
              tableRefs.expandedRows.value = value;
            }
          },
        };

        const serverProps = useServer
          ? {
              itemsLength: tableRefs.paginationInfo?.value?.totalCount || 0,
              page: tableRefs.pagination?.value?.page || 1,
              itemsPerPage: tableRefs.pagination?.value?.perPage || 20,
              "onUpdate:page": (page: number) => {
                if (tableRefs.pagination) {
                  tableRefs.pagination.value.page = page;
                }
              },
              "onUpdate:itemsPerPage": (perPage: number) => {
                if (tableRefs.pagination) {
                  tableRefs.pagination.value.perPage = perPage;
                }
              },
              sortBy: tableRefs.sortBy?.value,
              "onUpdate:sortBy": (sortBy: any) => {
                if (tableRefs.sortBy) {
                  tableRefs.sortBy.value = sortBy;
                }
              },
            }
          : {};

        return h(
          useServer ? ServerTable : Table,
          { ...baseProps, ...serverProps },
          {
            ...tableSlots,
            ...Object.fromEntries(
              tableRefs.headersFiltered.value.map(
                (header: HeaderType<T_Item>) => {
                  const name = `item.${header.key}`;
                  return [name, renderSlot(header, name)];
                },
              ),
            ),
          },
        );
      };
    },
  });
}
/*

<template>
  <!-- Custom header rendering with computeValue support -->
  <template
    v-for="header in table.headersFiltered.value"
    :key="header.key"
    v-slot:[`item.${header.key}`]="props"
  >
    
    <!--slot
        :name="`item.${header.key}`"
        v-bind="props">
      <v-chip
        v-if="header.computeValueColor"
        :color="header.computeValueColor(item)"
        size="small"
      >
        {{ getComputedValue(header, item, false) }}
      </v-chip>
      <template v-else>
        {{ getComputedValue(header, item, false) }}
      </template>
    </slot-->
  </template>

  <!-- Footer row -->
  <!--template v-if="table.footerItem.value" #body.append>
    <tr class="font-weight-bold">
      <td
        v-for="header in table.headersFiltered.value"
        :key="header.key"
        class="v-data-table__td v-data-table-column--align-start"
      >
        {{ getComputedValue(header, table.footerItem.value, false) }}
      </td>
    </tr>
  </template-->

  <!-- Expanded row with recursive child tables -->
  <!--template v-if="props.expandable" #expanded-row="{ columns, item }">
    <slot name="expanded-row" :columns="columns" :item="item">
      <tr v-if="item.childTables && item.childTables.length > 0">
        <td :colspan="columns.length" class="pa-0">
          <div class="pa-4 bg-grey-lighten-5">
            <div
              v-for="childTable in item.childTables"
              :key="childTable.id"
              class="mb-4"
            >
              <div class="d-flex justify-space-between align-center mb-2">
                <h4 class="text-subtitle-2">{{ childTable.id }}</h4>
                <v-btn
                  v-if="props.allowChildExport"
                  size="small"
                  variant="outlined"
                  prepend-icon="mdi-download"
                  @click="exportChildTable(childTable)"
                >
                  Export
                </v-btn>
              </div>
              <ReportingTable
                :table="childTable"
                :expandable="false"
                :is-child="true"
              />
            </div>
          </div>
        </td>
      </tr>
    </slot>
  </template-->
</template>
*/
